/* eslint-disable @typescript-eslint/no-explicit-any */
import { AcaLogLevel, log } from '@aca/core';
import { Injectable } from '@angular/core';
import { Observable, ReplaySubject, Subject } from 'rxjs';
import { ServerPushModel } from '../models/server.model';

/**
 * Bridges SignalR events (dispatched by the shell's SignalRService) to any
 * consumer in any MFE.
 *
 * Each operation key maps to a ReplaySubject(1, 5000):
 *  - Buffersize 1: late subscribers receive the most-recent event immediately
 *    on subscription, which closes the race window between a lazy-loaded MFE
 *    subscribing and an event that arrived during the load.
 *  - Window 5000 ms: the buffer expires after 5 seconds so stale events from
 *    a previous session or a different application are never replayed.
 */
@Injectable({
  providedIn: 'root'
})
export class ServerReceiveService {
  private operations = new Map<string, ReplaySubject<any>>();

  /**
   * Returns an Observable that emits events for the given SignalR operation.
   * Consumers should unsubscribe when done (e.g. via `untilDestroyed(this)`).
   * The @ServerListen decorator wraps this automatically.
   */
  public subscribe<T>(operation: string): Observable<T> {
    if (!this.operations.has(operation)) {
      this.operations.set(operation, new ReplaySubject<any>(1, 5000));
    }

    return this.operations.get(operation) as Observable<T>;
  }

  /**
   * ACADV internal - called by SignalRService to forward a received server event.
   * Creates the Subject on demand so events are never silently dropped even if
   * no component has subscribed yet.
   */
  public dispatch<T>(operation: string, payload?: ServerPushModel<T>) {
    log(AcaLogLevel.Debug, {}, ['acadv-push', 'Dispatch', operation]);
    if (!this.operations.has(operation)) {
      this.operations.set(operation, new ReplaySubject<any>(1, 5000));
    }
    (this.operations.get(operation) as Subject<unknown>).next(payload);
  }
}
